// Item/Competency Analysis — items rolled up to Learning Competencies, giving the
// two things the item-level report can't:
//   1. Per competency: who needs remediation, at which DepEd intervention tier.
//   2. Per learner: mastered vs least-mastered competencies.
// Both are derived on demand from results + answer key + the quiz's TOS.
import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import {
  analyzeCompetencies,
  leastMasteredFirst,
  type CompetencyGap,
  type CompetencyGapReport,
  type LearnerRef,
} from '../../analysis/competencyAnalysis.js';
import { formatItemSpec, shortDescription, MIN_ITEMS_FOR_LEARNER, MASTERY_CUTOFF } from '../../analysis/competencyMap.js';
import { buildCompetencyAnalysisCsv, buildLearnerMasteryCsv, shareOrDownload, safeName } from '../../export/csv.js';
import type { AnswerKeyRecord, CompetencyMapRecord, QuizRecord, ResultRecord, StudentRecord } from '../../db/schema.js';

/** Badge tone follows the DepEd bands: >=75 pass, 65-74 targeted, <65 sustained. */
const bandClass = (pct: number | null) =>
  pct == null ? 'badge-muted' : pct >= MASTERY_CUTOFF ? 'badge-ok' : pct >= 65 ? 'badge-warn' : 'badge-danger';

/** A labelled percentage bar. */
function bar(pct: number | null): string {
  if (pct == null) return `<span class="muted">—</span>`;
  const tone = pct >= MASTERY_CUTOFF ? 'var(--ok)' : pct >= 65 ? 'var(--warn)' : 'var(--danger)';
  return `<span class="gap-bar-wrap"><span class="gap-bar" style="width:${Math.max(1, pct)}%;background:${tone}"></span></span>`;
}

interface Ctx {
  quiz: QuizRecord;
  key: AnswerKeyRecord;
  map: CompetencyMapRecord;
  results: ResultRecord[];
  students: StudentRecord[];
  subtitle: string;
  back: string;
  fileTag: string;
  section: string;
  /** route to the printable / Excel version of the same scope. */
  reportsHref: string;
}

/** A named roster of learners under one instructional tier. */
function learnerRoster(title: string, learners: LearnerRef[], note: string): string {
  if (!learners.length) return '';
  return `<details class="gap-details"><summary>${esc(title)} (${learners.length})</summary>
    <p class="hint" style="margin:0 0 6px">${esc(note)}</p>
    <ul class="list">${learners
      .map(
        (l) => `<button class="row" data-result="${esc(l.resultId)}"><span class="grow">
          <span class="ttl">${esc(l.name)}</span>
          <span class="meta">${l.lrn ? `LRN ${esc(l.lrn)} · ` : ''}${l.earned}/${l.possible} correct</span></span>
          <span class="badge ${bandClass(l.pct)}">${l.pct}%</span><span class="chev">${icon.chevron()}</span></button>`,
      )
      .join('')}</ul></details>`;
}

/** The per-item half of the report, inside its competency. */
function itemBreakdown(c: CompetencyGap): string {
  if (!c.itemStats.length) return '';
  return `<details class="gap-details"><summary>Items (${c.itemStats.length})</summary>
    <div class="table-wrap"><table class="data"><thead><tr><th>Item</th><th class="num">Correct</th><th class="num">Item MPS</th><th>Below ${MASTERY_CUTOFF}%?</th></tr></thead>
      <tbody>${c.itemStats
        .map(
          (it) => `<tr><td>${it.q}</td><td class="num">${it.correct}/${it.total}</td><td class="num">${it.pct}%</td>
            <td>${it.pct < MASTERY_CUTOFF ? `<span class="badge badge-danger">Yes</span>` : ''}</td></tr>`,
        )
        .join('')}</tbody></table></div></details>`;
}

/** Report #1 — one card per competency, least mastered first. */
function competencySection(report: CompetencyGapReport): string {
  const ordered = leastMasteredFirst(report);
  if (!ordered.length) return `<p class="hint">No competency could be scored — the answer key covers none of the mapped items.</p>`;

  return ordered
    .map((c) => {
      // The class figure and the learner rosters are different claims. Say whose
      // response this is, and — when the class average passes but individuals
      // don't — reconcile the two outright instead of leaving them looking
      // contradictory.
      const action = c.classPct == null
        ? ''
        : `<div class="meta"><span class="muted">Class average:</span> <strong>${esc(c.intervention.classResponse)}</strong>${c.intervention.descriptor ? ` <span class="muted">· ${esc(c.intervention.descriptor)}</span>` : ''}</div>
           ${c.needing.length
             ? `<div class="meta danger-text"><strong>${c.needing.length} of ${c.n} learner${c.needing.length === 1 ? '' : 's'} still need${c.needing.length === 1 ? 's' : ''} remediation.</strong>${!c.intervention.needsRemediation ? ` <span class="muted">A learner can be below ${MASTERY_CUTOFF}% even when the class average is not.</span>` : ''}</div>`
             : ''}`;
      const rosters =
        learnerRoster('Structured, sustained intervention', c.sustained, 'Emerging / Nagsisimula — below 65%.') +
        learnerRoster('Targeted remediation', c.targeted, 'Developing / Napauunlad — 65-74%; scaffolding.') +
        learnerRoster('Guided practice', c.guidedPractice, `Connecting / Natutungo — 75-79%. Passing, so not remediation: practice for consistency.`);
      const allClear = !c.needing.length && !c.guidedPractice.length && c.n
        ? `<div class="meta muted">${icon.check()} Every learner is at 80% or above.</div>`
        : '';

      return `<div class="card"><div class="stack">
        <div><span class="mono">${c.label}</span>
          <span class="badge ${bandClass(c.classPct)}">${c.classPct == null ? '—' : `${c.classPct}%`} · ${esc(c.band)}</span>
          ${c.classPct == null ? '' : c.mastered ? `<span class="badge badge-ok">${icon.check()} Mastered</span>` : `<span class="badge badge-warn">Least mastered</span>`}</div>
        <div>${esc(c.description)}</div>
        ${bar(c.classPct)}
        ${action}
        <div class="meta muted">Items <span class="mono">${esc(formatItemSpec(c.items))}</span> (${c.items.length}) · ${c.possiblePerLearner} point${c.possiblePerLearner === 1 ? '' : 's'} each · ${c.masteredCount}/${c.n} learners at ${MASTERY_CUTOFF}%+${c.cognitiveLevel ? ` · ${esc(c.cognitiveLevel)}` : ''}</div>
        ${!c.reliable ? `<span class="badge badge-muted">${icon.alert()} Only ${c.items.length} item${c.items.length === 1 ? '' : 's'} — read the class figure, not individual learners.</span>` : ''}
        ${c.unkeyedItems.length ? `<span class="badge badge-warn">${icon.alert()} Items ${esc(formatItemSpec(c.unkeyedItems))} are not in the answer key and were excluded.</span>` : ''}
        ${itemBreakdown(c)}
        ${rosters}${allClear}
      </div></div>`;
    })
    .join('');
}

/** Report #2 — per learner, their mastered and least-mastered competencies. */
function learnerSection(report: CompetencyGapReport): string {
  if (!report.learners.length) return `<p class="hint">No scored papers yet.</p>`;
  return `<ul class="list">${report.learners
    .map((l) => {
      const weakest = l.leastMastered.slice(0, 3);
      const strongest = l.mastered.slice(0, 3);
      const detail = `<details class="gap-details"><summary>${esc(l.name)} · ${l.score}/${l.maxScore} (${l.overallPct}%) · ${l.mastered.length} mastered, ${l.leastMastered.length} to work on</summary>
        <div class="table-wrap"><table class="data"><thead><tr><th></th><th>Learning competency</th><th class="num">Score</th><th class="num">%</th><th>Level</th><th>Instructional response</th></tr></thead>
          <tbody>${l.scores
            .map(
              (s) => `<tr><td class="mono">${s.label}</td>
                <td style="white-space:normal;min-width:220px">${esc(s.description)}</td>
                <td class="num">${s.pct == null ? '—' : `${s.earned}/${s.possible}`}</td>
                <td class="num">${s.pct == null ? '—' : `${s.pct}%`}</td>
                <td><span class="badge ${bandClass(s.pct)}">${esc(s.band)}</span>${!s.reliable && s.pct != null ? ` <span class="badge badge-muted">few items</span>` : ''}</td>
                <td style="white-space:normal;min-width:180px">${s.pct == null ? '—' : esc(s.intervention.response)}</td></tr>`,
            )
            .join('')}</tbody></table></div>
        <div class="row2" style="margin-top:10px">
          <button class="btn btn-ghost btn-sm" data-result="${esc(l.resultId)}">${icon.doc()} Open paper</button>
          ${l.studentId ? `<button class="btn btn-ghost btn-sm" data-student="${esc(l.studentId)}">${icon.user()} Student history</button>` : ''}
        </div></details>`;
      return `<li class="gap-learner">
        ${detail}
        <div class="gap-chips">
          ${weakest.length
            ? `<span class="muted">Least mastered:</span> ${weakest.map((s) => `<span class="badge ${bandClass(s.pct)}" title="${esc(s.description)}">${s.label} ${s.pct}% · ${esc(shortDescription(s.description, 32))}</span>`).join(' ')}`
            : `<span class="muted">Least mastered: none — every competency at ${MASTERY_CUTOFF}%+</span>`}
        </div>
        <div class="gap-chips">
          ${strongest.length
            ? `<span class="muted">Mastered:</span> ${strongest.map((s) => `<span class="badge badge-ok" title="${esc(s.description)}">${s.label} ${s.pct}%</span>`).join(' ')}${l.mastered.length > 3 ? ` <span class="muted">+${l.mastered.length - 3} more</span>` : ''}`
            : `<span class="muted">Mastered: none yet</span>`}
        </div>
      </li>`;
    })
    .join('')}</ul>`;
}

function renderReport(c: Ctx): RenderedView {
  const report = analyzeCompetencies(c.results, c.key, c.map, c.students);
  const leastMastered = report.competencies.filter((x) => x.classPct != null && !x.mastered);
  const needingAny = new Set(report.competencies.flatMap((x) => x.needing.map((l) => l.resultId)));

  const summary = `<div class="card"><div class="stack">
    <div><span class="big-mps">${leastMastered.length}</span><span class="muted"> of ${report.competencies.length} least-mastered competencies</span></div>
    <div class="muted" style="font-size:14px">${report.n} learner${report.n === 1 ? '' : 's'} · passing mark ${report.cutoff}% · ${needingAny.size} learner${needingAny.size === 1 ? '' : 's'} need remediation on at least one competency</div>
    ${report.unmappedItems.length
      ? `<span class="badge badge-warn">${icon.alert()} Items ${esc(formatItemSpec(report.unmappedItems))} belong to no competency</span>`
      : ''}
    ${report.emptyCompetencies.length
      ? `<span class="badge badge-warn">${icon.alert()} No scored items: ${esc(report.emptyCompetencies.join(', '))}</span>`
      : ''}
    ${report.n < 10 ? `<span class="badge badge-muted">Small class (n=${report.n}) — treat class percentages as indicative.</span>` : ''}
  </div></div>`;

  return {
    title: 'Item/Competency Analysis',
    subtitle: c.subtitle,
    back: c.back,
    html: `<div class="view">
      ${summary}
      <p class="section-label">${icon.chart()} Per competency — who needs remediation</p>
      ${competencySection(report)}
      <p class="section-label" style="margin-top:22px">${icon.user()} Per learner — mastered &amp; least mastered</p>
      ${learnerSection(report)}
      <p class="hint">${report.cutoff}% is DepEd's passing mark. Below it the instructional response scales: 65-74% targeted remediation with scaffolding, below 65% structured sustained intervention. 75-79% (Connecting / Natutungo) is passing — guided practice for consistency, not remediation — so it is listed separately.
        Blanks and double-marks score zero, exactly as in the total score. Competencies with under ${MIN_ITEMS_FOR_LEARNER} items are marked — too few items to judge one learner.</p>
      <p class="section-label" style="margin-top:22px">Export</p>
      <div class="stack">
        <button class="btn btn-primary btn-block" data-reports>${icon.doc()} Print / Excel (A4, tables)</button>
        <button class="btn btn-ghost btn-block" data-dl="competency">${icon.download()} Competency analysis CSV</button>
        <button class="btn btn-ghost btn-block" data-dl="learners">${icon.download()} Per-learner mastery CSV</button>
      </div>
      <p class="hint">CSV is for spreadsheets and TESTIFY hand-off; use Print / Excel for the copy that gets filed.</p>
      <button class="btn btn-ghost btn-block" data-edit style="margin-top:14px">${icon.edit()} Edit the Table of Specifications</button>
    </div>`,
    mount(root) {
      onClick(root, '[data-result]', (el) => navigate(`#/result/${el.dataset.result}`));
      onClick(root, '[data-student]', (el) => navigate(`#/student/${el.dataset.student}`));
      onClick(root, '[data-edit]', () => navigate(`#/quiz/${c.quiz.id}/competencies`));
      onClick(root, '[data-reports]', () => navigate(c.reportsHref));
      onClick(root, '[data-dl="competency"]', async () => {
        const how = await shareOrDownload(`${safeName(c.quiz.title)}_${c.fileTag}_competency_analysis.csv`, buildCompetencyAnalysisCsv(c.quiz, report, c.section));
        toast(how === 'shared' ? 'Shared competency analysis' : 'Downloaded competency analysis');
      });
      onClick(root, '[data-dl="learners"]', async () => {
        const how = await shareOrDownload(`${safeName(c.quiz.title)}_${c.fileTag}_learner_mastery.csv`, buildLearnerMasteryCsv(c.quiz, report, c.section));
        toast(how === 'shared' ? 'Shared learner mastery' : 'Downloaded learner mastery');
      });
    },
  };
}

/** Shared empty states: no key, or no TOS yet. */
function notReady(quiz: QuizRecord, back: string, subtitle: string, reason: 'key' | 'map'): RenderedView {
  const body =
    reason === 'key'
      ? `<div class="card"><span class="badge badge-warn">${icon.alert()} No answer key</span>
           <p style="margin:12px 0 0">Set the answer key first — competency results are computed from scored items.</p></div>
         <button class="btn btn-primary btn-block" data-key style="margin-top:12px">${icon.key()} Set answer key</button>`
      : `<div class="card"><div class="stack"><span class="badge badge-warn">${icon.alert()} No Table of Specifications</span>
           <p style="margin:0">This quiz has no item → competency map yet, so results can only be reported per item.
             Add your TOS — each learning competency with the <strong>item numbers</strong> that measure it — and this report fills in.</p></div></div>
         <button class="btn btn-primary btn-block" data-map style="margin-top:12px">${icon.plus()} Add the Table of Specifications</button>`;
  return {
    title: 'Item/Competency Analysis',
    subtitle,
    back,
    html: `<div class="view">${body}</div>`,
    mount(root) {
      onClick(root, '[data-key]', () => navigate(`#/quiz/${quiz.id}/key`));
      onClick(root, '[data-map]', () => navigate(`#/quiz/${quiz.id}/competencies`));
    },
  };
}

/** One class's run of a quiz — the actionable view for a class adviser. */
export async function assignmentGapsView(params: Record<string, string>): Promise<RenderedView> {
  const assignment = await repos.getAssignment(params.id);
  if (!assignment) return { title: 'Item/Competency Analysis', back: '/quizzes', html: `<div class="view"><p class="empty">Not found.</p></div>` };
  const quiz = (await repos.getQuiz(assignment.quizId))!;
  const [key, map, results, students, cls] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.competencyMapFor(quiz.id),
    repos.resultsForAssignment(assignment.id),
    repos.studentsInClass(assignment.classId),
    repos.getClass(assignment.classId),
  ]);
  const subtitle = `${quiz.title} · ${cls?.name ?? ''}`;
  const back = `/assignment/${assignment.id}/review`;
  if (!key) return notReady(quiz, back, subtitle, 'key');
  if (!map?.competencies.length) return notReady(quiz, back, subtitle, 'map');
  return renderReport({
    quiz,
    key,
    map,
    results,
    students,
    subtitle,
    back,
    fileTag: safeName(cls?.name ?? 'class'),
    section: cls?.name ?? '',
    reportsHref: `#/assignment/${assignment.id}/reports`,
  });
}

/** Rolled up across every class that sat this quiz. */
export async function quizGapsView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Item/Competency Analysis', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const [key, map, assignments] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.competencyMapFor(quiz.id),
    repos.assignmentsForQuiz(quiz.id),
  ]);
  const subtitle = `${quiz.title} · all classes`;
  const back = `/quiz/${quiz.id}`;
  if (!key) return notReady(quiz, back, subtitle, 'key');
  if (!map?.competencies.length) return notReady(quiz, back, subtitle, 'map');
  const results = await repos.resultsForQuiz(quiz.id);
  const students = (await Promise.all(assignments.map((a) => repos.studentsInClass(a.classId)))).flat();
  return renderReport({ quiz, key, map, results, students, subtitle, back, fileTag: 'all', section: 'ALL', reportsHref: `#/quiz/${quiz.id}/reports` });
}
